import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';

import { DsTextComponent } from '@design-system/ds-text/component/ds-text.component';
import { DsButtonComponent } from '@design-system/ds-button/component/ds-button.component';
import { DsIconButtonComponent } from '@design-system/ds-icon-button/component/ds-icon-button.component';
import { DsRadioGroupComponent } from '@design-system/ds-radio-group/component/ds-radio-group.component';
import { DsRadioGroupOption } from '@design-system/ds-radio-group/interfaces/ds-radio-group-option.interface';
import { DsRadioGroupChange } from '@design-system/ds-radio-group/interfaces/ds-radio-group-change.interface';
import { ORGAN_RECOVERY_CONFIRMATION_QUESTION } from '@modals/organ-recovery-confirmation/properties/organ-recovery-confirmation-modal.properties';

/**
 * Data accepted when the modal is opened to review an answer that is already stored.
 *
 * Omitted entirely (or `currentAnswer: null`) for the completion-guard flow, where by
 * definition no answer exists yet.
 */
export interface OrganRecoveryConfirmationModalData {
  /** The stored answer to preselect. `null`/`undefined` means unanswered - opens unset. */
  readonly currentAnswer?: boolean | null;
}

/**
 * Standalone DS modal that asks whether the organ was recovered and transported.
 *
 * The component owns no HTTP/business logic - it only returns the user's answer via
 * `afterClosed()`: `true`/`false` on Submit, `undefined` for every dismissal route
 * (Cancel, the header close icon, and Escape). A backdrop click is NOT a dismissal route:
 * `OrganRecoveryConfirmationService` opens this modal with `disableClose: true` and wires
 * Escape back by hand, so a stray click outside cannot drop a required answer.
 *
 * Prefill: the modal preselects `data.currentAnswer` when one is supplied, so the Actions
 * menu can show and change an answer already on the record. This supersedes the original
 * never-prefill decision (D4), which predated the Actions-menu entry. The completion-guard
 * flow is unaffected - it passes no data, so it still opens unset and Submit stays inert
 * until an explicit Yes/No is chosen. Note that opening with a stored answer means Submit is
 * enabled immediately, which is intended: re-submitting the same value is a valid no-op save.
 */
@Component({
  selector: 'app-organ-recovery-confirmation-modal',
  standalone: true,
  imports: [FormsModule, DsTextComponent, DsButtonComponent, DsIconButtonComponent, DsRadioGroupComponent],
  templateUrl: './organ-recovery-confirmation-modal.component.html',
  styleUrls: ['./organ-recovery-confirmation-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrganRecoveryConfirmationModalComponent {
  private readonly dialogRef = inject(MatDialogRef<OrganRecoveryConfirmationModalComponent, boolean | undefined>);
  private readonly data = inject<OrganRecoveryConfirmationModalData | null>(MAT_DIALOG_DATA, { optional: true });

  readonly question = ORGAN_RECOVERY_CONFIRMATION_QUESTION;

  readonly options: DsRadioGroupOption[] = [
    { value: true, label: 'Yes' },
    { value: false, label: 'No' },
  ];

  /**
   * Seeded from the stored answer when one was passed in, otherwise unset.
   *
   * Compared against `null`/`undefined` rather than tested for truthiness, because `false`
   * ("No") is a real stored answer and must preselect just like `true`.
   */
  selectedAnswer: boolean | null = this.data?.currentAnswer ?? null;

  get isSubmitDisabled(): boolean {
    return this.selectedAnswer === null;
  }

  /**
   * Syncs the selected answer from the radio group's change event.
   */
  onAnswerChange(change: DsRadioGroupChange): void {
    this.selectedAnswer = change.value as boolean | null;
  }

  /**
   * Closes with `undefined` - the single "do not proceed" dismissal signal.
   */
  onClose(): void {
    this.dialogRef.close(undefined);
  }

  /**
   * Closes with `undefined` - the single "do not proceed" dismissal signal.
   */
  onCancel(): void {
    this.dialogRef.close(undefined);
  }

  /**
   * Closes with the chosen boolean answer. Guarded so Submit can never resolve
   * without an explicit selection, even if triggered while nominally disabled.
   */
  onSubmit(): void {
    if (this.selectedAnswer === null) {
      return;
    }

    this.dialogRef.close(this.selectedAnswer);
  }
}
